import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Trash2 } from "lucide-react";

import { api, type PurgeResult } from "../../api";
import { useScope } from "../../components/ScopeContext";
import { Note, SettingsSection } from "./shared";

/**
 * Stored data: removing captured detections, scoped from one camera up to
 * everything. Never touches camera or recorder configuration.
 */
export function DataSection() {
  const qc = useQueryClient();
  const { stores } = useScope();
  const cams = useQuery({ queryKey: ["cameras", "all"], queryFn: () => api.cameras.list() });
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const onScopedDone = (what: string) => {
    setBusy(null);
    setNote(`Deleted captured data for ${what}.`);
    qc.invalidateQueries({ queryKey: ["search"] });
    qc.invalidateQueries({ queryKey: ["storage-usage"] });
  };

  const purgeCamera = useMutation({
    mutationFn: (id: string) => api.admin.purgeCamera(id),
    onSuccess: (_r, id) => onScopedDone(`camera ${id}`),
    onError: () => setBusy(null),
  });
  const purgeStore = useMutation({
    mutationFn: (s: string) => api.admin.purgeStore(s),
    onSuccess: (_r, s) => onScopedDone(`store “${s}”`),
    onError: () => setBusy(null),
  });

  return (
    <SettingsSection
      title="Stored data"
      description="Remove captured detections (the searchable history and saved snapshots). Cameras, recorders and accounts are never touched."
    >
      {note && <Note tone="success">{note}</Note>}

      <div className="bf-card space-y-1">
        <h3 className="text-sm font-semibold text-white">By store</h3>
        <p className="pb-1 text-xs text-bf-muted">
          Deletes data for every camera assigned to the store.
        </p>
        {stores.length === 0 && (
          <p className="py-2 text-sm text-bf-muted">No stores yet.</p>
        )}
        {stores.map((s) => (
          <ScopedDeleteRow
            key={s.name}
            label={s.display_name || s.name}
            sublabel={`${s.camera_count} camera${s.camera_count === 1 ? "" : "s"}`}
            confirmText="Delete all data for this store?"
            isPending={busy === `store:${s.name}` && purgeStore.isPending}
            onConfirm={() => {
              setBusy(`store:${s.name}`);
              purgeStore.mutate(s.name);
            }}
          />
        ))}
      </div>

      <div className="bf-card space-y-1">
        <h3 className="text-sm font-semibold text-white">By camera</h3>
        <p className="pb-1 text-xs text-bf-muted">Deletes data for one camera.</p>
        {(cams.data ?? []).length === 0 && (
          <p className="py-2 text-sm text-bf-muted">No cameras configured.</p>
        )}
        {(cams.data ?? []).map((c) => (
          <ScopedDeleteRow
            key={c.id}
            label={c.name || c.id}
            sublabel={c.store ? `Store: ${c.store}` : undefined}
            confirmText="Delete all data for this camera?"
            isPending={busy === `cam:${c.id}` && purgeCamera.isPending}
            onConfirm={() => {
              setBusy(`cam:${c.id}`);
              purgeCamera.mutate(c.id);
            }}
          />
        ))}
      </div>

      <DangerZone />
    </SettingsSection>
  );
}

function ScopedDeleteRow({
  label,
  sublabel,
  confirmText,
  onConfirm,
  isPending,
}: {
  label: string;
  sublabel?: string;
  confirmText: string;
  onConfirm: () => void;
  isPending: boolean;
}) {
  const [confirming, setConfirming] = useState(false);

  return (
    <div className="flex items-center justify-between gap-3 border-t border-white/5 py-2 first:border-t-0">
      <div className="min-w-0">
        <div className="truncate text-sm text-bf-text">{label}</div>
        {sublabel && <div className="text-xs text-bf-muted">{sublabel}</div>}
      </div>
      {!confirming ? (
        <button
          type="button"
          className="shrink-0 text-xs text-bf-muted hover:text-red-400"
          onClick={() => setConfirming(true)}
        >
          <Trash2 size={15} className="mr-1 inline" /> Delete data
        </button>
      ) : (
        <div className="flex shrink-0 items-center gap-2">
          <span className="text-xs text-bf-muted">{confirmText}</span>
          <button
            type="button"
            className="text-xs text-bf-muted hover:text-bf-text"
            onClick={() => setConfirming(false)}
            disabled={isPending}
          >
            cancel
          </button>
          <button
            type="button"
            className="rounded bg-red-600/80 px-2 py-1 text-xs text-white hover:bg-red-500 disabled:opacity-40"
            onClick={() => {
              onConfirm();
              setConfirming(false);
            }}
            disabled={isPending}
          >
            {isPending ? "Deleting…" : "Confirm"}
          </button>
        </div>
      )}
    </div>
  );
}

function DangerZone() {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState("");
  const [result, setResult] = useState<PurgeResult | null>(null);

  const purge = useMutation({
    mutationFn: api.admin.purgeData,
    onSuccess: (r) => {
      setResult(r);
      setConfirming(false);
      setTyped("");
      qc.invalidateQueries({ queryKey: ["search"] });
      qc.invalidateQueries({ queryKey: ["storage-usage"] });
    },
  });

  return (
    <div className="bf-card space-y-3 border border-red-500/30 bg-red-500/5">
      <div className="flex items-center gap-2 text-red-300">
        <AlertTriangle size={18} />
        <h3 className="text-sm font-semibold">Danger zone</h3>
      </div>
      <p className="text-sm text-bf-muted">
        Permanently delete <strong>all captured data</strong> from every store:
        the searchable history, saved snapshots, in-progress activity and
        temporary files. Cameras, recorders and accounts are kept. This cannot
        be undone.
      </p>

      {result && (
        <Note tone="success">
          Done — cleared the searchable history, {result.minio_objects_deleted}{" "}
          saved photo{result.minio_objects_deleted === 1 ? "" : "s"}, and{" "}
          {result.inflight_files_deleted ?? 0} temporary file
          {(result.inflight_files_deleted ?? 0) === 1 ? "" : "s"}.
        </Note>
      )}

      {purge.isError && (
        <Note tone="error">
          {(purge.error as Error).message || "Delete failed."}
        </Note>
      )}

      {!confirming ? (
        <div className="flex justify-end">
          <button
            type="button"
            className="bf-button bg-red-600 bg-none hover:bg-red-500"
            onClick={() => {
              setResult(null);
              setConfirming(true);
            }}
          >
            <Trash2 size={16} className="mr-2" /> Delete all captured data
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <label className="block text-sm">
            Type <code className="font-mono text-red-300">DELETE</code> to confirm:
            <input
              className="bf-input mt-1"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoFocus
              placeholder="DELETE"
            />
          </label>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="bf-button bg-white/10 bg-none"
              onClick={() => {
                setConfirming(false);
                setTyped("");
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="bf-button bg-red-600 bg-none hover:bg-red-500 disabled:opacity-40"
              disabled={typed !== "DELETE" || purge.isPending}
              onClick={() => purge.mutate()}
            >
              {purge.isPending ? "Deleting…" : "Permanently delete"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
